import { APPEARANCE, Text as NText, type Props as NTextProps } from '@natera/material/lib/text'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { cn } from '~/shared/lib/cn'
import { Tooltip } from '~/shared/ui/local-tooltip'

type AppearanceType = keyof typeof APPEARANCE

interface TextProps extends Omit<NTextProps, 'appearance'> {
  className?: string
  appearance: AppearanceType
  placeholder?: string
  truncated?: boolean
  withTooltip?: boolean
  copyable?: boolean
  copyTooltipSide?: 'top' | 'bottom'
}

export const Text = forwardRef<HTMLSpanElement, TextProps>(
  (
    {
      children,
      className,
      appearance,
      placeholder,
      truncated = false,
      withTooltip = false,
      copyable = false,
      copyTooltipSide = 'top',
      ...rest
    },
    ref,
  ) => {
    const internalTextRef = useRef<HTMLSpanElement>(null)
    const textRef = ref || internalTextRef
    const [isActuallyTruncated, setIsActuallyTruncated] = useState(false)
    const isActuallyCopyable = copyable && !!children
    const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false)
    const [isCopied, setIsCopied] = useState(false)
    const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
      const currentRef = typeof textRef === 'function' ? null : textRef.current
      if (currentRef) {
        setIsActuallyTruncated(currentRef.scrollWidth > currentRef.clientWidth)
      }
    }, [children, textRef])

    useEffect(() => {
      return () => {
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current)
        }
      }
    }, [])

    const textContent = (
      <span
        ref={textRef}
        className={cn(
          'inline-flex',
          {
            'block truncate overflow-hidden': truncated,
            'cursor-pointer': isActuallyCopyable,
          },
          className,
        )}
        onClick={() => {
          if (isActuallyCopyable) {
            const currentRef = typeof textRef === 'function' ? null : textRef.current
            navigator.clipboard.writeText(currentRef?.textContent || '')
            setIsCopied(true)
            setIsCopyTooltipOpen(true)
            if (copyTimeoutRef.current) {
              clearTimeout(copyTimeoutRef.current)
            }
            copyTimeoutRef.current = setTimeout(() => {
              setIsCopied(false)
              setIsCopyTooltipOpen(false)
            }, 800)
          }
        }}
      >
        <NText
          {...rest}
          appearance={appearance ? APPEARANCE[appearance] : undefined}
        >
          {children || placeholder}
        </NText>
      </span>
    )

    if (truncated && withTooltip && isActuallyTruncated) {
      return (
        <Tooltip
          content={isCopied ? 'Copied!' : children}
          open={isCopyTooltipOpen}
          onOpenChange={setIsCopyTooltipOpen}
          side={copyTooltipSide}
        >
          {textContent}
        </Tooltip>
      )
    } else if (isActuallyCopyable) {
      return (
        <Tooltip
          content={isCopied ? 'Copied!' : 'Copy'}
          open={isCopyTooltipOpen}
          onOpenChange={setIsCopyTooltipOpen}
          side={copyTooltipSide}
        >
          {textContent}
        </Tooltip>
      )
    }

    return textContent
  },
)

Text.displayName = 'Text'
